import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useNotice } from "@/hooks/use-notices";
import { usePermissions } from "@/hooks/use-permissions";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { noticeStatusVariant } from "@/components/notices/notice-status";
import { LetterPdfPreview } from "@/components/notices/letter-pdf-preview";
import { NoticeEnclosuresCard } from "@/components/notices/enclosures/notice-enclosures-card";
import { NoticeActions } from "@/components/notices/notice-actions";
import { knownAddressSource } from "@/components/notices/address-source";
import { letterEmailFallback } from "@/components/notices/email-fallback";
import { RecipientCard } from "@/components/notices/recipient-card";
import { RecipientDrawer } from "@/components/notices/recipient-drawer";
import { snapshotHasStreet } from "@/components/notices/recipient-draft";
import { useLetterAccount } from "@/components/notices/use-letter-account";
import { SendHistoryCard } from "@/components/notices/send-history-card";
import { NoticeIdentityRows } from "@/components/notices/notice-identity-rows";
import { NoticePairStatus } from "@/components/notices/notice-pair-status";
import { Badge, Breadcrumbs, Card, EmptyValue, PageHeader, PageSkeleton } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/notices/$id/")({
  component: LetterDetailPage,
});

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Letter detail — the officer's review surface and the deep-link target the
 * queue drawer, the reverse-lookup history cards and the records projection all
 * point at. Left column: the identity <dl> (what the letter is and what it is
 * about), the recipient + copies card, the report forms the letter encloses,
 * the withdrawal record on a cancelled letter, and the send-event ledger. Right
 * column: the copy-of-record PDF (and the packet, when the letter encloses
 * anything) and the same action row the drawer offers, so neither surface can
 * offer a verb the other refuses.
 *
 * While a send is still ahead of the letter (`draft` / `approved`) the
 * recipient card warns when the frozen address has no street, linking the
 * letter's account — its own for an account letter, its premise's flagged
 * correspondence account for a premise or assembly letter, none for an address
 * the officer entered — read only while that warning names one. A caller
 * holding `notices:update` also gets Edit recipient there, which opens the
 * recipient drawer.
 *
 * Over the same stretch Send may go by email instead, at the officer's choice.
 * Whether it may is the letter's email fact: its recipient's email address and
 * the letter's own `print_only` (the server's verdict; a print-only letter is
 * refused on any other channel). The action row offers the choice and the
 * no-street warning says the same fact.
 */
function LetterDetailPage() {
  const { t } = useTranslation(["notices", "common"]);
  const { id } = Route.useParams();
  const { can } = usePermissions();
  const [recipientOpen, setRecipientOpen] = useState(false);

  const { data: notice, isLoading, isError } = useNotice(id);
  const preDispatch = notice?.status === "draft" || notice?.status === "approved";
  // The card names an account only in its no-street warning, and never for an
  // address the officer entered, so nothing is read for any other letter.
  const warningNamesAccount =
    notice !== undefined &&
    preDispatch &&
    !snapshotHasStreet(notice.recipient) &&
    knownAddressSource(notice.recipient.address_source) !== "override";
  const account = useLetterAccount(notice, warningNamesAccount);
  useRecentPageTitle(notice?.subject ?? undefined);

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !notice) {
    return (
      <div className="p-8">
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("errors.loadDetailFailed")}
        </div>
        <div className="mt-4">
          <Link to="/notices" className="text-sm text-primary hover:underline">
            {t("common:back")}
          </Link>
        </div>
      </div>
    );
  }

  const canEditRecipient = preDispatch && can("notices", "update");
  const email = letterEmailFallback(notice);

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("letters.title"), to: "/notices" },
          { label: t("letters.detail.title") },
        ]}
      />
      <PageHeader
        title={notice.subject ?? t("letters.detail.title")}
        actions={
          <Badge variant={noticeStatusVariant(notice.status)}>
            {t(`letters.status.${notice.status}`)}
          </Badge>
        }
        meta={notice.companion ? <NoticePairStatus notice={notice} /> : undefined}
      />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-5">
          <Card title={t("letters.detail.identityTitle")}>
            <dl className="space-y-2 text-sm">
              <NoticeIdentityRows notice={notice} />
              <div>
                <dt className="text-muted">{t("letters.detail.identity.generatedAt")}</dt>
                <dd className="mt-0.5 text-on-surface">{formatDate(notice.generated_at)}</dd>
              </div>
              <div>
                <dt className="text-muted">{t("letters.detail.identity.approvedAt")}</dt>
                <dd className="mt-0.5 text-on-surface">
                  {notice.approved_at ? formatDate(notice.approved_at) : <EmptyValue />}
                </dd>
              </div>
            </dl>
          </Card>

          <RecipientCard
            recipient={notice.recipient}
            ccRecipients={notice.cc_recipients}
            account={account}
            warnNoStreet={preDispatch}
            email={email}
            onEditRecipient={canEditRecipient ? () => setRecipientOpen(true) : undefined}
          />
          {canEditRecipient && (
            <RecipientDrawer
              open={recipientOpen}
              onClose={() => setRecipientOpen(false)}
              notice={notice}
            />
          )}

          <NoticeEnclosuresCard
            noticeId={notice.id}
            status={notice.status}
            enclosures={notice.enclosures}
          />

          {/* Why a letter was withdrawn is the regulatory record of the
              withdrawal, so it is shown, not merely stored. */}
          {notice.status === "cancelled" && (
            <Card title={t("letters.detail.cancelledTitle")}>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-muted">{t("letters.detail.cancelled.at")}</dt>
                  <dd className="mt-0.5 text-on-surface">
                    {notice.cancelled_at ? formatDate(notice.cancelled_at) : <EmptyValue />}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted">{t("letters.detail.cancelled.reason")}</dt>
                  <dd className="mt-0.5 whitespace-pre-wrap text-on-surface">
                    {notice.cancelled_reason ?? <EmptyValue />}
                  </dd>
                </div>
              </dl>
            </Card>
          )}

          <SendHistoryCard sends={notice.sends} enclosureCount={notice.enclosures.length} />
        </div>

        <div className="space-y-6 lg:col-span-7">
          <NoticeActions notice={notice} emailFallback={email} />
          <LetterPdfPreview
            noticeId={notice.id}
            pdfStoragePath={notice.pdf_storage_path}
            hasEnclosures={notice.enclosures.length > 0}
          />
        </div>
      </div>
    </div>
  );
}
